import { useHistory, useLocation } from 'react-router-dom';

import PATHS from 'router/PATHS';

type Config = {
  redirectKey?: string;
  fallback?: string;
  moveBack?: boolean;
};

const defaultConfig: Config = {
  redirectKey: 'redirect_url',
  fallback: PATHS.ROOT,
  moveBack: false,
};

const isSafeRedirect = (url: string): boolean => {
  if (!url || !url.startsWith('/') || url.startsWith('//')) return false;
  try {
    const testUrl = new URL(url, window.location.origin);
    return testUrl.origin === window.location.origin;
  } catch {
    return false;
  }
};

export const sanitizeRedirectPath = (redirectValue?: string | null, fallback: string = PATHS.ROOT): string => {
  if (!redirectValue) return fallback;
  if (!redirectValue.startsWith('/')) return fallback;
  if (redirectValue.startsWith('//')) return fallback;
  if (redirectValue.includes('\\')) return fallback;

  return isSafeRedirect(redirectValue) ? redirectValue : fallback;
};

const useRedirectFromQuery = () => {
  const { search } = useLocation();
  const history = useHistory();

  const getRedirectValue = (config = defaultConfig) => {
    const { redirectKey, fallback } = { ...defaultConfig, ...config };
    const urlSearchParams = new URLSearchParams(search);
    const redirectUrl = urlSearchParams.get(redirectKey as string);
    return sanitizeRedirectPath(redirectUrl, fallback || PATHS.ROOT);
  };

  const redirect = (config = defaultConfig) => {
    if (config.moveBack) return history.goBack();
    const redirectUrl = getRedirectValue(config);
    return history.replace(redirectUrl);
  };

  return { redirect, getRedirectValue };
};

export default useRedirectFromQuery;
